<template>
  <div ref="wrapperRef" class="flex justify-between items-start tags" :style="initSty">
    <div class="flex-1 flex">
      <div
        class="max-w-[4em] text-14 leading-[32px] mr-[28px] whitespace-nowrap text-left text-font-60 z-10 items-start"
      >
        {{ data?.tag_name }}
      </div>
      <div
        ref="containerRef"
        class="flex-y-center flex-wrap gap-[10px_14px] text-14 text-font-60 select-none transition-all overflow-hidden justify-start"
        :style="more.height"
      >
        <TagItem
          v-for="x in dictList"
          :key="x.tag_id"
          :data-tag-id="x.tag_id"
          :data="x"
          :checks="checks"
          @change="onUpdateData"
        />
      </div>
    </div>
    <div
      v-show="showMore"
      class="tags-more flex pl-5 text-14 leading-[32px] text-font-60 text-right0 cursor-pointer hover:text-font-1"
      @click="handleMore"
    >
      {{ more.text }}
      <iconpark-icon :name="more.expand ? 'arrowdown' : 'arrowright'" />
    </div>
  </div>
</template>

<script lang="ts" setup>
/**
 * class.tags-more 为了响应父级 CSS :hover
 */
import type { Tag } from '~/api/interface';
import TagItem from './tagItem.vue';

interface Positions {
  [key: string]: number; // 值为上边框距离父元素顶部距离
}
const { $t } = useNuxtApp();
const props = defineProps<{
  modelValue?: Tag[];
  multiple?: boolean;
  data?: Tag;
  hide?: boolean;
  sticky?: boolean; // 借助是否“吸顶”处理高度
  parentHovered?: boolean; // 借助父元素 hover 处理高度
}>();
const emit = defineEmits(['update:modelValue', 'change']);

const initSty = props.hide ? 'display: none' : '';
const wrapperRef = ref();

const dictList = computed(() => {
  const all: Tag = {
    tag_id: props?.data?.tag_id || -1,
    ids: `${props?.data?.level},${props?.data?.tag_id}`,
    tag_name: $t('quan-bu'),
    level: props?.data?.level || 0,
    children: [],
  };
  return [all, ...(props.data?.children || []).filter(v => !!v.filter_valid)];
});

const BaseHeight = '32px';
const positions: Positions = {};

const resourceLocation = injectResourceLocation();

const showMore = ref(false);
const containerRef = ref<HTMLElement>();

const more = reactive({
  text: $t('geng-duo'),
  expand: false,
  height: {
    height: BaseHeight,
  },
});

const containerHeight = computed(() => `${containerRef.value?.scrollHeight}px`);

function observeStyleChange() {
  const observer = new MutationObserver(() => {
    const style = window.getComputedStyle(wrapperRef.value);
    if (style.display !== 'none') {
      // 触发计算“更多”
      showMore.value = calcMore();
    }
  });
  observer.observe(wrapperRef.value, {
    attributes: true,
    attributeFilter: ['style'],
  });
}

// 每次选择后判断是否要自动展开（不收起）
function updateHeight() {
  if (!containerRef.value) {
    return;
  }
  const pRect = containerRef.value.getBoundingClientRect();
  const $positions: Positions = {};
  [...containerRef.value.children].forEach(child => {
    const node = child as HTMLElement;
    const key = node.getAttribute('data-tag-id'); // 依赖标签熟悉 data-tag-id
    const cRect = node.getBoundingClientRect();
    if (!key) {
      console.log('标签 tag_id 错误');
      return;
    }
    $positions[key] = cRect.top - pRect.top;
  });
  if (Object.keys(positions).length !== 0) {
    const keys = Object.keys($positions);
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      if ($positions[key] > positions[key]) {
        autoOpenMore();
        break;
      }
    }
  }
  Object.assign(positions, $positions);
}

// 回显检查是否需要展开
function checkExpand() {
  if (!containerRef.value) {
    return;
  }
  const pRect = containerRef.value.getBoundingClientRect();
  const lineBreakTags = [...containerRef.value.children].filter(child => {
    const node = child as HTMLElement;
    const cRect = node.getBoundingClientRect();
    return cRect.top > pRect.top;
  });
  const childrenBeenSelected = lineBreakTags.some(t => t.querySelectorAll('.check-tag').length > 0);
  if (childrenBeenSelected) {
    autoOpenMore();
  }
}

const route = useRoute();

watch(
  () => route.query.tag_ids,
  () => {
    nextTick(() => {
      checkExpand();
    });
  },
);

watch(
  () => dictList.value.length,
  () => {
    nextTick(() => {
      showMore.value = calcMore();
    });
  },
);

function handleMore() {
  more.expand = !more.expand;
  more.text = more.expand ? $t('shou-qi') : $t('geng-duo');
  more.height.height = more.expand ? containerHeight.value : BaseHeight;
}

function autoOpenMore() {
  more.expand = true;
  more.text = $t('shou-qi');
  more.height.height = containerHeight.value;
}

// 查找子元素是否有换行
function calcMore() {
  if (containerRef.value) {
    const pRect = containerRef.value.getBoundingClientRect();
    return [...containerRef.value.children].some(node => {
      const cRect = (node as HTMLElement).getBoundingClientRect();
      return cRect.top - pRect.top;
    });
  }
  return false;
}

const onUpdateData = (data: Tag | Tag[], checked: boolean, isRoute: boolean = false) => {
  let checksArr = checks.value;
  const eachArr = Array.isArray(data) ? data : [data];
  if (
    eachArr?.[0]?.tag_name === $t('quan-bu') ||
    checksArr?.[0]?.tag_name === $t('quan-bu') ||
    isRoute ||
    !props.multiple
  ) {
    checksArr = [];
  }

  eachArr.forEach((t: Tag) => {
    const index = checksArr.findIndex(c => c.ids === t.ids);
    if (checked && index === -1) {
      checksArr.push(t);
    }

    if (!checked && index !== -1) {
      checksArr.splice(index, 1);
    }
  });
  if (!checksArr.length) {
    checksArr = [dictList.value[0]];
  }

  checks.value = checksArr;

  if (!isRoute) {
    trackEvent(TRACK_EVENT.CLICK_TEMPLATE_CENTER_TAG_FILTER, {
      source: resourceLocation?.source,
      tag_name: props.data?.tag_name,
      tag_id: props.data?.tag_id,
      children_tag_ids: eachArr.map(item => item.tag_name).join(','),
      children_tag_names: eachArr.map(item => item.tag_name).join(','),
    });
  }
  nextTick(() => {
    updateHeight();
  });
};

const checks = computed({
  get: () => {
    return props.modelValue || [dictList.value[0]];
  },
  set: v => {
    emit('update:modelValue', v);
    emit('change', v);
  },
});

let HeightBeforeSticky = BaseHeight; // 吸顶前的高度
watch(
  () => props.sticky,
  _sticky => {
    if (_sticky) {
      more.height.height = BaseHeight;
    } else {
      more.height.height = HeightBeforeSticky;
    }
  },
);
watch(
  () => more.expand,
  _expand => {
    if (_expand) {
      more.height.height = containerHeight.value;
    } else {
      more.height.height = BaseHeight;
    }
    HeightBeforeSticky = more.height.height;
  },
);
watch(
  () => props.parentHovered,
  _hovered => {
    if (!props.sticky) {
      return;
    }
    if (_hovered) {
      more.height.height = HeightBeforeSticky;
    } else {
      more.height.height = BaseHeight;
    }
  },
);

onMounted(() => {
  showMore.value = calcMore();
  observeStyleChange();
  nextTick(() => {
    updateHeight(); // 首次记录
    checkExpand();
  });
});
</script>

<style lang="scss" scoped></style>
